'use client'

import { alpha, Chip, type Theme } from '@mui/material'
import { type MouseEvent, type ReactElement } from 'react'

// Sized and coloured to match FilterDropdown's trigger chip (same height,
// weight, radius, and state colours) so a boolean toggle sits flush in a
// filter row beside the dropdown chips. Off = neutral outlined chip; on =
// primary-outlined with a faint wash, mirroring the dropdown's active state.
// `field.border` is a teach palette key; other themes fall back to MUI's outlined-input border.
interface PaletteWithField {
  field?: { border: string }
}

const toggleChipSx = {
  fontWeight: 600,
  borderRadius: 1,
  fontSize: '0.75rem',
  height: 28,
  '&.MuiChip-outlined': { boxShadow: 'none' },
  '& .MuiChip-icon': { fontSize: 16 },
}

export function FilterToggle({
  label,
  icon,
  selected = false,
  onClick,
  'aria-pressed': ariaPressed,
}: {
  label: string
  icon?: ReactElement
  selected?: boolean
  onClick?: (event: MouseEvent<HTMLElement>) => void
  'aria-pressed'?: boolean
}) {
  return (
    <Chip
      label={label}
      icon={icon}
      variant="outlined"
      color={selected ? 'primary' : 'default'}
      onClick={onClick}
      aria-pressed={ariaPressed}
      sx={[
        toggleChipSx,
        !selected && {
          // Matches the outlined TextField/SearchField next to it.
          '&.MuiChip-outlinedDefault': {
            backgroundColor: 'background.paper',
            borderColor: (theme: Theme) =>
              (theme.palette as PaletteWithField).field?.border ??
              'rgba(0, 0, 0, 0.23)',
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          },
        },
        selected &&
          ((theme) => ({
            '&.MuiChip-colorPrimary': {
              background: alpha(theme.palette.primary.main, 0.1),
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.dark,
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.16),
              },
            },
          })),
      ]}
    />
  )
}
